import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Chip, CircularProgress, IconButton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { zonedNow } from "../../utils/clienttimezone";

// ── PALETTE ──────────────────────────────────────────────────────────────────
// Same hues as the PBI pages so a metric reads the same colour wherever it
// appears. Ops adds three STATUS colours, which the PBI palette has no
// equivalent for — those exist to answer "is this okay", not "which metric".
export const BLUE  = "#378ADD";
export const GREEN = "#1D9E75";
export const AMBER = "#BA7517";
export const RED   = "#A32D2D";
export const GRAY  = "#888780";
export const NAVY  = "#0C447C";
export const BROWN = "#8A5524";
export const PINK  = "#D4537E";
export const AVG_INK = "#0F6E56";

export const OK   = GREEN;
export const WARN = AMBER;
export const BAD  = RED;

// ── DEFAULTS ─────────────────────────────────────────────────────────────────
// This Mo + Day, matching the PBI pages. A rolling "30D" answers a different
// question every morning; a month-shaped window can be checked against the
// scorecard and the emailed report.
// ── DEFAULTS ─────────────────────────────────────────────────────────────────
// 30m + Min, because the common reason to open Ops is that a campaign is
// running right now. The day-level presets are still one click away for the
// look-back tabs.
//
// ── TWO KINDS OF RANGE, AND THEY FILTER DIFFERENTLY ──
// Intraday presets ("30m", "4h") are RELATIVE TO NOW — no calendar boundary is
// involved, so they resolve to a plain UTC instant pair and need no zone math
// at all. Day-level presets ("This Mo") are CALENDAR windows and must resolve
// in the tenant's zone or a range picked at 11pm lands on the wrong day.
//
// getDateRange returns { startTs, endTs } for the first and
// { startDate, endDate } for the second. The backend branches on which arrived.
export const GRAN_OPTIONS  = ["Min", "Hour", "Day", "Week", "Month"];
export const RANGE_OPTIONS = [
  "15m", "30m", "1h", "2h", "4h", "8h", "12h",
  "1D", "3D", "7D", "30D",
  "C Wk", "This Mo",
];
// Divider positions in the pill row: intraday | rolling days | calendar.
export const RANGE_GROUPS  = [7, 4, 2];

// ── 12h / Hour ──
// A shift, bucketed hourly. 30m/Min answered "is it working this minute",
// which is the Agent Health tab's job and it refreshes itself — the ranged
// tabs are opened to see the shape of a working day, and a 30-minute window
// is too narrow to have a shape.
//
// It also matches the Agent Health snapshot, which counts over 12 hours. Two
// tabs disagreeing about what "recent" means is how a reader ends up
// comparing numbers that were never comparable.
//
// Hour rather than Min at this width: 12h at minute granularity is 720 points
// on an axis roughly 700px wide, so the line becomes a solid band. Twelve
// hourly points is a shape you can read.
export const DEFAULT_RANGE = "12h";
export const DEFAULT_GRAN  = "Hour";

// Minutes per intraday preset. Presence in this map is what marks a preset as
// relative-to-now rather than calendar-based.
const INTRADAY_MINUTES = {
  "15m": 15, "30m": 30, "1h": 60, "2h": 120, "4h": 240, "8h": 480, "12h": 720,
};

export const isIntradayRange = (preset) => preset in INTRADAY_MINUTES;

// A minute-bucketed 8h window is 480 points. Recharts renders it, but the axis
// needs preserveStartEnd or the labels collide into a smear.
export const isSubDayGran = (gran) =>
  ["min", "minute", "hour"].includes(String(gran).toLowerCase());

// ── Formatting ───────────────────────────────────────────────────────────────
export const safeNum = (v) => { const n = Number(v); return isNaN(n) ? 0 : n; };

export function fmtInt(n) {
  if (n == null || isNaN(n)) return "—";
  return Number(n).toLocaleString();
}

export function fmtPct(v, dec = 1) {
  const n = Number(v);
  if (v == null || isNaN(n)) return "—";
  return `${n.toFixed(dec)}%`;
}

// Seconds read as seconds up to two minutes, then minutes. A response time of
// "0.12 mins" is unreadable; "7 sec" is the number people actually quote.
export function fmtDuration(secs) {
  if (secs == null || isNaN(secs)) return "—";
  const s = Number(secs);
  if (s < 120) return `${s.toFixed(s < 10 ? 1 : 0)} sec`;
  if (s < 5400) return `${(s / 60).toFixed(1)} min`;
  return `${(s / 3600).toFixed(1)} hr`;
}

export function fmtDate(val) {
  if (!val) return "";
  const m = typeof val === "string" && val.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(val);
  if (isNaN(d)) return String(val);
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" });
}

// ── A BUCKET LABEL IS WALL CLOCK, AN EVENT TIME IS AN INSTANT ──
//
// Two kinds of timestamp come back from the Ops API and they must be handled
// differently:
//
//   summary_date   a bucket, "2026-08-16 14:00:00". ALREADY converted to the
//                  tenant zone in SQL. Read the digits; converting again
//                  shifts every label by the tenant offset.
//
//   receivedUtc,   a true UTC instant. Convert ONCE, with fmtTime.
//   eventAtUtc, …
//
// The bug this replaces was subtle in the worst way: the shifted labels were
// still plausible times, in order, evenly spaced. Nothing looked wrong except
// that the busy hour was in the wrong place.
//
// Parsed literally rather than through Date, so neither the driver's UTC
// stamping nor the browser's zone can touch it.
function parseWallClock(val) {
  if (val == null) return null;

  // Belt and braces: if a datetime ever comes back as a Date rather than a
  // style-120 string, its UTC parts ARE the wall clock the server produced.
  if (val instanceof Date) {
    if (isNaN(val)) return null;
    return {
      y: val.getUTCFullYear(), mo: val.getUTCMonth(), d: val.getUTCDate(),
      h: val.getUTCHours(), mi: val.getUTCMinutes(),
    };
  }

  const m = String(val).match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);
  if (!m) return null;
  return {
    y: +m[1], mo: +m[2] - 1, d: +m[3],
    h: m[4] != null ? +m[4] : 0,
    mi: m[5] != null ? +m[5] : 0,
  };
}

function clockLabel({ h, mi }, withMinutes) {
  const ampm = h < 12 ? "AM" : "PM";
  const h12  = h % 12 === 0 ? 12 : h % 12;
  return withMinutes ? `${h12}:${String(mi).padStart(2, "0")} ${ampm}` : `${h12} ${ampm}`;
}

// Axis labels. At Min/Hour granularity summary_date is a full timestamp, so a
// date label would print the same string on every point — the time is the only
// thing that varies.
//
// `timeZone` is accepted and deliberately UNUSED: the value arrived already
// converted. It stays in the signature so a caller passing it is not silently
// wrong the day someone adds a zone-dependent branch.
export function fmtAxisDate(val, gran = "day", _timeZone) {
  const p = parseWallClock(val);
  if (!p) return val ? String(val) : "";
  const g = String(gran).toLowerCase();

  if (g === "min" || g === "minute") return clockLabel(p, true);
  if (g === "hour")                  return clockLabel(p, false);

  const d = new Date(p.y, p.mo, p.d);
  if (g === "month")
    return d.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
  const wd = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"][d.getDay()];
  return `${wd} ${d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit" })}`;
}

// Table cells. Same split as the axis, but with the date kept alongside the
// time — a row in a scrollable table has no neighbours to give it context.
export function fmtPeriod(val, gran = "day", _timeZone) {
  const p = parseWallClock(val);
  if (!p) return "—";
  const g = String(gran).toLowerCase();
  if (g === "min" || g === "minute" || g === "hour") {
    const d = new Date(p.y, p.mo, p.d);
    const date = d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit" });
    return `${date}, ${clockLabel(p, g !== "hour")}`;
  }
  return fmtDate(val);
}

// The "· 14/08/26 – 15/08/26" suffix on every chart title. Reads the params
// object rather than the preset, so it says what the range RESOLVED to — a
// screenshot carries no pill state.
export function formatRangeLabel(params, timeZone) {
  if (params?.startTs && params?.endTs) {
    const f = (v) => {
      try {
        return new Date(v).toLocaleTimeString("en-US", {
          timeZone, hour: "numeric", minute: "2-digit", hour12: true,
        });
      } catch { return String(v).slice(11, 16); }
    };
    return `${f(params.startTs)} – ${f(params.endTs)}`;
  }
  return `${fmtDate(params?.startDate)} – ${fmtDate(params?.endDate)}`;
}

// UTC timestamps from the API, rendered in the tenant zone.
export function fmtTime(utc, timeZone) {
  if (!utc) return "—";
  const d = new Date(utc);
  if (isNaN(d)) return String(utc);
  try {
    return d.toLocaleString("en-US", {
      timeZone, month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
    });
  } catch { return d.toISOString(); }
}

export function fullName(row) {
  const n = [row?.firstName, row?.lastName].filter(Boolean).join(" ").trim();
  return n || "—";
}

export function fmtPhone(p) {
  const s = String(p || "").replace(/\D/g, "");
  if (s.length === 10) return `(${s.slice(0, 3)}) ${s.slice(3, 6)}-${s.slice(6)}`;
  return p || "—";
}

function toLocalYMD(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Intraday presets are relative to NOW, so they need no zone math at all — a
// "last 30 minutes" window is the same 30 minutes in every zone, and the raw
// UTC column can be filtered directly. Only the calendar presets have to
// resolve their boundaries in the tenant's zone.
export function getDateRange(preset, timeZone) {
  const mins = INTRADAY_MINUTES[preset];
  if (mins) {
    const end = new Date();
    const start = new Date(end.getTime() - mins * 60000);
    return { startTs: start.toISOString(), endTs: end.toISOString() };
  }

  const now = zonedNow(timeZone);
  const y = now.getFullYear(), m = now.getMonth();
  const dow = now.getDay();
  const thisMonday = new Date(now);
  thisMonday.setDate(now.getDate() - ((dow + 6) % 7));
  thisMonday.setHours(0, 0, 0, 0);

  let start, end = now;
  switch (preset) {
    case "1D":  start = new Date(now); start.setDate(now.getDate() - 1);  break;
    case "3D":  start = new Date(now); start.setDate(now.getDate() - 3);  break;
    case "7D":  start = new Date(now); start.setDate(now.getDate() - 7);  break;
    case "30D": start = new Date(now); start.setDate(now.getDate() - 30); break;
    case "C Wk":     start = thisMonday; break;
    case "This Mo":
    default:         start = new Date(y, m, 1); break;
  }
  return { startDate: toLocalYMD(start), endDate: toLocalYMD(end) };
}

// ── Auto-refresh ─────────────────────────────────────────────────────────────
// Pauses when the tab is hidden. A monitor left open on a spare screen
// overnight would otherwise poll ~5,700 times before anyone looked at it.
export function useAutoRefresh(fn, intervalMs, enabled = true) {
  const saved = useRef(fn);
  useEffect(() => { saved.current = fn; }, [fn]);

  useEffect(() => {
    if (!enabled || !intervalMs) return;
    let timer = null;
    const tick = () => { if (!document.hidden) saved.current(); };
    timer = setInterval(tick, intervalMs);
    const onVis = () => { if (!document.hidden) saved.current(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", onVis); };
  }, [intervalMs, enabled]);
}

// ── Components ───────────────────────────────────────────────────────────────
export function SectionTitle({ children, right }) {
  return (
    <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mt: 2, mb: 1 }}>
      <Typography sx={{
        fontSize: 11, fontWeight: 500, color: "text.secondary",
        textTransform: "uppercase", letterSpacing: "0.06em",
      }}>
        {children}
      </Typography>
      {right && <Typography sx={{ fontSize: 11, color: "text.disabled" }}>{right}</Typography>}
    </Box>
  );
}

export function Caption({ children }) {
  return (
    <Typography sx={{ fontSize: 11, color: "text.disabled", mt: 0.75, lineHeight: 1.45 }}>
      {children}
    </Typography>
  );
}

// `status` drives the border and figure colour: "ok" | "warn" | "bad" | null.
// Only pass it where a number has a defensible threshold — colouring a plain
// count green implies a judgement the number does not support.
export function CounterCard({ label, value, sub, color = BLUE, status = null, big = false }) {
  const statusColor = status === "bad" ? BAD : status === "warn" ? WARN : status === "ok" ? OK : null;
  const ink = statusColor || color;
  return (
    <Box sx={{
      background: "var(--mui-palette-background-default, #f7f7f7)",
      borderRadius: 2, p: big ? "14px 16px" : "10px 14px", minWidth: 0,
      border: "1px solid",
      borderColor: statusColor ? `${statusColor}55` : "transparent",
      ...(status === "bad" ? { background: `${BAD}0D` } : {}),
    }}>
      <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontSize: big ? 30 : 22, fontWeight: 500, color: ink, lineHeight: 1.15 }}>
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: 11, color: "text.disabled", mt: 0.5 }}>{sub}</Typography>
      )}
    </Box>
  );
}

export function ChartCard({ title, right, children, height = 220 }) {
  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, p: "12px 14px" }}>
      {(title || right) && (
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, mb: 1 }}>
          <Typography sx={{
            fontSize: 11, fontWeight: 500, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.05em",
          }}>{title}</Typography>
          {right && (
            <Typography sx={{ fontSize: 11, color: AVG_INK, whiteSpace: "nowrap" }}>{right}</Typography>
          )}
        </Box>
      )}
      <Box sx={{ width: "100%", height: height === "auto" ? "auto" : height }}>{children}</Box>
    </Box>
  );
}

export function PillGroup({ label, options, value, onChange, groups }) {
  // `groups` are run lengths, e.g. [6,4,2] draws a divider after the 6th and
  // the 10th pill. Twelve range presets in one undifferentiated row is a wall;
  // the dividers say "these are minutes, these are days, these are calendar".
  const boundaries = new Set();
  if (groups) {
    let idx = 0;
    groups.forEach((size, gi) => {
      if (gi < groups.length - 1) { idx += size; boundaries.add(idx); }
    });
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography sx={{
        fontSize: 11, color: "text.secondary",
        textTransform: "uppercase", letterSpacing: "0.05em",
      }}>{label}</Typography>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
        {options.map((opt, i) => {
          const active = value === opt;
          return (
            <React.Fragment key={opt}>
            {boundaries.has(i) && (
              <Box sx={{ width: "1px", height: 18, bgcolor: "divider", mx: 0.5, flexShrink: 0 }} />
            )}
            <Box onClick={() => onChange(opt)} sx={{
              px: "10px", py: "3px", fontSize: 12, borderRadius: "20px",
              cursor: "pointer", userSelect: "none",
              border: "0.5px solid",
              borderColor: active ? BLUE : "divider",
              background: active ? BLUE : "transparent",
              color: active ? "#fff" : "text.secondary",
              fontWeight: active ? 500 : 400,
              transition: "all .15s",
              "&:hover": { borderColor: BLUE },
            }}>{opt}</Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}

// The slicer bar. No campaign select and no Texts/Emails toggle — Ops watches
// the pipeline, which has neither dimension.
export function OpsSlicers({ gran, setGran, range, setRange, tzLabel, right }) {
  return (
    <Box sx={{
      display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-end",
      pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
    }}>
      <PillGroup label="Granularity" options={GRAN_OPTIONS} value={gran} onChange={setGran} />
      <PillGroup label="Date range"  options={RANGE_OPTIONS} value={range} onChange={setRange} groups={RANGE_GROUPS} />
      <Box sx={{ ml: "auto", alignSelf: "flex-end", display: "flex", alignItems: "center", gap: 2 }}>
        {right}
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
          Times shown in {tzLabel}
        </Typography>
      </Box>
    </Box>
  );
}

export function StatusChip({ label, tone = "default" }) {
  const map = { ok: OK, warn: WARN, bad: BAD, default: GRAY };
  const c = map[tone] || GRAY;
  return (
    <Chip label={label} size="small" sx={{
      height: 20, fontSize: 10, fontWeight: 500,
      color: c, background: `${c}1A`, border: `0.5px solid ${c}55`,
      "& .MuiChip-label": { px: "6px" },
    }} />
  );
}

export function LoadingRow({ text = "Loading…" }) {
  return (
    <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 2 }}>
      <CircularProgress size={18} />
      <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{text}</Typography>
    </Box>
  );
}

// ── DETAIL TABLE ─────────────────────────────────────────────────────────────
// `columns` is [{ key, label, width, align, render, mono }]. Every account-level
// table on these pages puts Account / Customer / Phone first, in that order, so
// a row is identifiable before anything else is read.
export function DetailTable({ columns, rows, emptyText = "Nothing to show.", maxHeight = 420, dense = true }) {
  if (!rows || rows.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: "center", fontSize: 12, color: "text.disabled" }}>
        {emptyText}
      </Box>
    );
  }
  return (
    <TableContainer sx={{ maxHeight, border: "0.5px solid", borderColor: "divider", borderRadius: 2 }}>
      <Table stickyHeader size={dense ? "small" : "medium"}>
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableCell key={c.key} align={c.align || "left"} sx={{
                fontSize: 10, fontWeight: 600, textTransform: "uppercase",
                letterSpacing: "0.05em", color: "text.secondary",
                background: "var(--mui-palette-background-paper, #fff)",
                whiteSpace: "nowrap", width: c.width,
              }}>{c.label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={row.turnId ?? row.accountId ?? i} hover>
              {columns.map((c) => (
                <TableCell key={c.key} align={c.align || "left"} sx={{
                  fontSize: 12, py: 0.75,
                  fontFamily: c.mono ? "ui-monospace, SFMono-Regular, Menlo, monospace" : undefined,
                  maxWidth: c.width || 260,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }} title={typeof row[c.key] === "string" ? row[c.key] : undefined}>
                  {c.render ? c.render(row) : (row[c.key] ?? "—")}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}


// The drill-open column. Sits first on every account table so the affordance is
// in one place and learned once. Requires the row to carry customerdataid —
// the detail queries select it for exactly this.
export function drillColumn(onOpen) {
  return {
    key: "__drill", label: "", width: 44, align: "center",
    render: (r) => (
      r.customerdataid ? (
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onOpen(r); }}
          sx={{ width: 24, height: 24 }}
          title="View the full conversation"
        >
          <OpenInNewIcon sx={{ fontSize: 14 }} />
        </IconButton>
      ) : null
    ),
  };
}

// The three identity columns every account table opens with. Spread these in
// rather than re-declaring them, so the ordering and formatting stay identical
// across pages.
export const identityColumns = [
  { key: "accountId", label: "Account", width: 110, mono: true,
    render: (r) => r.accountId ?? "—" },
  { key: "customer",  label: "Customer", width: 170,
    render: (r) => fullName(r) },
  { key: "phone",     label: "Phone", width: 130, mono: true,
    render: (r) => fmtPhone(r.phone) },
];

// ── Data hook ────────────────────────────────────────────────────────────────
// One loader for a page's fetches. Returns { data, loading, error, reload }.
// `deps` are the params; the fetchers receive them.
export function useOpsData(fetchers, params, ready = true) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const keys = useMemo(() => Object.keys(fetchers), [fetchers]);

  const reload = useCallback(async () => {
    if (!ready) return;
    setError("");
    try {
      const results = await Promise.all(
        keys.map((k) => fetchers[k](params).catch(() => null))
      );
      const next = {};
      keys.forEach((k, i) => { next[k] = results[i]; });
      setData(next);
    } catch (e) {
      setError("Failed to load monitoring data");
      console.error(e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params), ready, keys.join("|")]);

  useEffect(() => { reload(); }, [reload]);

  return { data, loading, error, reload };
}